// Page-visit timeline for the Conversation right rail.
//
// Shows the pages the visitor has viewed this/recent session, newest first.
// Seeded from the DB on open (persistent) and kept live by `visitor:page`
// bus events (see lib/bus-bridge). Each row links to the full URL, opening in
// a new tab. The page need not be publicly reachable — it's an eyesight into
// what the visitor is browsing, so a dead/auth-gated link is acceptable.

import { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { VisitorId } from '@prakash-software/shared';
import { usePageVisitsStore } from '../stores/page-visits';
import { fetchPageVisits } from '../lib/page-visits-api';
import { formatRelativeOrAbsolute } from '../lib/datetime';
import { pageHref } from '../lib/page-link';
import styles from './PageTimeline.module.css';

export function PageTimeline({ visitorId }: { visitorId: VisitorId }) {
  const visits = usePageVisitsStore((s) => s.byVisitor.get(visitorId));
  const setAll = usePageVisitsStore((s) => s.setAll);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed from the DB whenever the conversation's visitor changes. Live
  // `visitor:page` events append on top via the store afterwards.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPageVisits(visitorId)
      .then((pages) => {
        if (!cancelled) setAll(visitorId, pages);
      })
      .catch(() => {
        if (!cancelled) setError('Couldn’t load the page history.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visitorId, setAll]);

  return (
    <section className={styles.root} aria-labelledby="pages-heading">
      <span className={styles.label}>Page History</span>

      {error ? <p className={styles.meta}>{error}</p> : null}
      {!error && (!visits || visits.length === 0) ? (
        <p className={styles.meta} id="pages-heading">
          {loading ? 'Loading…' : 'No pages tracked yet.'}
        </p>
      ) : null}
      {visits && visits.length > 0 ? (
        <ol className={styles.list} id="pages-heading">
          {visits.map((v, i) => {
            // Visitor-reported: only http(s) URLs become links (lib/page-link.ts).
            const href = pageHref(v.url);
            return (
              <li key={`${v.at}-${i}`} className={styles.row}>
                {href ? (
                  <a className={styles.link} href={href} target="_blank" rel="noopener noreferrer" title={v.url}>
                    <span className={styles.label}>{pageLabel(v.url)}</span>
                    <ExternalLink className={styles.icon} size={12} aria-hidden />
                  </a>
                ) : (
                  <span className={styles.link} title={v.url}>
                    <span className={styles.label}>{pageLabel(v.url)}</span>
                  </span>
                )}
                <span className={styles.time}>{formatRelativeOrAbsolute(v.at)}</span>
              </li>
            );
          })}
        </ol>
      ) : null}
    </section>
  );
}

// A compact, readable label for a URL: "path?query" (no origin), falling back
// to the raw string if it doesn't parse. The full URL is always in the
// link's title + href.
function pageLabel(url: string): string {
  try {
    const u = new URL(url);
    const tail = `${u.pathname}${u.search}${u.hash}`;
    return tail && tail !== '/' ? tail : u.hostname;
  } catch {
    return url;
  }
}
